import type {TagColor} from "~/components/tag/tag.styles";
import type {RiskSignalImpactType} from "~/pages/supplier-360/types/Supplier360Types";

/** Rows per infinite-scroll load. */
export const SIGNAL_FEED_PAGE_SIZE = 15;


export const CATEGORIES_BY_IMPACT: Record<RiskSignalImpactType, string[]> = {
    'Business Change': ['Ownership changes', 'Senior Management Changes'],
    'Business Growth': ['Business Expansion', 'ESG Opportunity', 'Financial Opportunity', 'Innovation'],
    'Business Risk': [
        'Cyber Risk',
        'ESG Risk',
        'Financial Risk',
        'Health, Safety, and Environment',
        'Operational Risk',
        'Quality Issues',
        'Regulatory compliance Issues',
    ],
    'Governments': ['Geo-Political Issues', 'Sanctions'],
    'World': ['Natural Disasters'],
};

export const IMPACT_TYPES = Object.keys(CATEGORIES_BY_IMPACT) as RiskSignalImpactType[];

/** The design feed's languages, in its own order. */
const LANGUAGE_CODES = ['EN', 'CN', 'ES', 'FR', 'IT', 'JA', 'DE', 'CZ', 'RU', 'KO', 'PT', 'AR'];

/** The design's `SX_LANGS`. */
const LANGUAGE_NAMES: Record<string, string> = {
    EN: 'English', DE: 'German', CN: 'Chinese', ES: 'Spanish', FR: 'French', IT: 'Italian',
    JA: 'Japanese', RU: 'Russian', KO: 'Korean', PT: 'Portuguese', CZ: 'Czech', AR: 'Arabic',
};

export function languageName(code: string): string {
    return LANGUAGE_NAMES[code] ?? code;
}

export type FilterOption = {value: string, label: string};

export const IMPACT_TYPE_OPTIONS: FilterOption[] = IMPACT_TYPES.map(v => ({value: v, label: v}));

export const LANGUAGE_OPTIONS: FilterOption[] = LANGUAGE_CODES.map(code => ({value: code, label: languageName(code)}));

/** The categories of the picked impact types — all of them when none is picked. */
export function categoryOptions(impactTypes: RiskSignalImpactType[]): FilterOption[] {
    const types = impactTypes.length ? impactTypes : IMPACT_TYPES;
    return types.flatMap(t => CATEGORIES_BY_IMPACT[t]).map(c => ({value: c, label: c}));
}

export const IMPACT_TYPE_COLOR: Record<RiskSignalImpactType, TagColor> = {
    'Business Risk': 'error',
    'Business Growth': 'success',
    'Business Change': 'info',
    'Governments': 'secondary',
    'World': 'default',
};

/** The feed's `tone` is `bad` / `good` / `info` (§2.10). */
export function toneColor(tone: string): TagColor {
    if (tone === 'bad') return 'error';
    if (tone === 'good') return 'success';
    return 'info';
}

export type SignalFeedFilterState = {
    impactTypes: RiskSignalImpactType[];
    categories: string[];
    languages: string[];
};

export const EMPTY_SIGNAL_FEED_FILTERS: SignalFeedFilterState = {impactTypes: [], categories: [], languages: []};

/** `"2026-07-13"` → `"13 Jul 2026"`, the design's one absolute-date format. */
export function formatSignalDate(isoDate: string | null): string {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
}
